import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { Members, Participants } from '@/pages/setup-fund/utils/type';

import { AdminDirectors } from '../fund/types';
import { apiFundViewsFetch } from '../index';
import {
  FundCarouselRes,
  FundFiltersMetadata,
  FundSearchParams,
  FundSearchResults,
  FundSumsRes,
} from './types';

export const FUND_VIEWS_QUERY_KEYS = {
  adminTeam: 'admin-team',
  carousel: 'carousel',
  filters: 'filters',
  search: 'search',
  sums: 'sums',
  portfolio: 'portfolio',
  similarFunds: 'similar-funds',
};

type FundAdminTeamRes = {
  members: Members[];
  directors: AdminDirectors[];
  participants: Participants[];
};

export const _getFundAdminTeam = (fundId: string) => {
  return queryOptions({
    queryKey: [FUND_VIEWS_QUERY_KEYS.adminTeam, fundId],
    queryFn: () => apiFundViewsFetch<FundAdminTeamRes>(`/admin/${fundId}/team`),
    refetchOnMount: true,
  });
};

export const _getFundViewsFilters = () => {
  return queryOptions({
    queryKey: [FUND_VIEWS_QUERY_KEYS.filters],
    queryFn: () => apiFundViewsFetch<FundFiltersMetadata>('/filters'),
    refetchOnMount: true,
  });
};

export const _getFundViewsSearchInfinite = (params?: Omit<FundSearchParams, 'page'>) => {
  return infiniteQueryOptions({
    queryKey: [FUND_VIEWS_QUERY_KEYS.search, 'infinite', params],
    queryFn: ({ pageParam = 1, signal }) => {
      const queryParams = params
        ? {
            ...params,
            category: params.category?.join(','),
            stage: params.stage?.join(','),
            lockUp: params.lockUp?.join(','),
            assets: params.assets?.join(','),
            strategies: params.strategies?.join(','),
            page: pageParam,
            limit: params.limit || 100,
          }
        : { page: pageParam, limit: 100 };

      return apiFundViewsFetch<FundSearchResults>('/search', { query: queryParams, signal });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination.page < lastPage.pagination.totalPages) {
        return lastPage.pagination.page + 1;
      }
      return undefined;
    },
    refetchOnMount: true,
  });
};

export const _getFundViewsSums = () => {
  return queryOptions({
    queryKey: [FUND_VIEWS_QUERY_KEYS.sums],
    queryFn: () => apiFundViewsFetch<FundSumsRes>('/sums'),
    refetchOnMount: true,
  });
};

export type PortfolioPosition = {
  id: string;
  asset: {
    id: string;
    type: string;
    name: string;
    slug: string;
    isApproved: boolean;
  };
  amount: string;
  denomination: string;
  costBasis: number;
  costBasisInUsd: number;
  value: number;
  totalInvestedInUsd: number;
  valueInUsd: number;
  createdAt: string;
  updatedAt: string;
};

export type PortfolioRes = {
  positions: PortfolioPosition[];
  numberOfPositions: number;
  totalInvestedInUsd: number;
  totalBalanceInUsd: number;
  totalProfitInUsd: number;
  createdAt: string;
  updatedAt: string;
};

export const _getPortfolio = () => {
  return queryOptions({
    queryKey: [FUND_VIEWS_QUERY_KEYS.portfolio],
    queryFn: () => apiFundViewsFetch<PortfolioRes>(`/portfolio`),
    refetchOnMount: true,
    retry: 1,
  });
};

export type SimilarFund = {
  fundId: string;
  fundName: string;
  slug: string;
  entityId?: string;
  entityName?: unknown;
  entityLogoUrl?: unknown;
  coverImageUrl?: unknown;
  cardImageUrl?: unknown;
  category?: unknown;
  strategies?: string[];
  assets?: string[];
  aumInMillionsUsd?: unknown;
  targetReturnRate?: unknown;
  fundApprovalStatus?: string;
  investmentAvailabilityStatus?: string;
  similarityScore?: number;
  matchedCriteria?: string[];
  navTrend?: unknown;
};

export type SimilarFundsRes = {
  sourceFundId: string;
  similarFunds: SimilarFund[];
  total: number;
};

export const _getSimilarFunds = (sourceFundId?: string, limit: number = 4) => {
  return queryOptions({
    enabled: !!sourceFundId,
    queryKey: [FUND_VIEWS_QUERY_KEYS.similarFunds, sourceFundId, limit],
    queryFn: () =>
      apiFundViewsFetch<SimilarFundsRes>(`/similar/${sourceFundId}`, { query: { limit } }),
    refetchOnMount: true,
    retry: 1,
  });
};

///////////////////////////////////////////////

export const _getFundCarousel = () => {
  return queryOptions({
    queryKey: [FUND_VIEWS_QUERY_KEYS.carousel],
    queryFn: async () => {
      const res = await apiFundViewsFetch<FundCarouselRes>('/carousel');
      // TODO: remove once the API renames "Recommended" -> "Highlighted" (GP-2905)
      return {
        ...res,
        items: res.items.map((item) =>
          item.viewName === 'Recommended' ? { ...item, viewName: 'Highlighted' } : item,
        ),
      };
    },
    refetchOnMount: true,
  });
};
